import React from 'react';
import { UnscheduledEvent } from '../../types';
import { CAT_OPTIONS, CAT_DUE_DATE_OFFSET_DAYS } from '../../constants';
import { addDaysToDateZulu } from '../../utils/dateUtils';

/**
 * Props for {@link DeferredSection}.
 */
interface DeferredSectionProps {
  formData: UnscheduledEvent;
  isReadOnly: boolean;
  updateField: (field: keyof UnscheduledEvent, value: string | number | boolean) => void;
  /** Highlights the Category/CAT groups as required when nothing is selected. */
  fieldsRequired?: boolean;
}

/**
 * Renders the Deferred-record-specific reference, category flags, and CAT
 * severity selector.
 */
const DeferredSection: React.FC<DeferredSectionProps> = ({
  formData,
  isReadOnly,
  updateField,
  fieldsRequired,
}) => {
  const categoryMissing =
    !!fieldsRequired &&
    !(
      formData.apuDeferral ||
      formData.etopsRestricted ||
      formData.operationallyRestrictive ||
      formData.catIiiRestricted
    );
  const catMissing = !!fieldsRequired && !formData.catType;

  /**
   * Toggles the selected CAT and, for CATs with an automatic offset (B/C/D),
   * auto-populates the Due Date as Time Declared + offset days. CAT A and TRK
   * have no offset, so the Due Date stays manual. The field remains editable
   * afterwards either way.
   */
  const handleCatChange = (cat: string): void => {
    const next = formData.catType === cat ? '' : cat;
    updateField('catType', next);
    const offsetDays = CAT_DUE_DATE_OFFSET_DAYS[next];
    if (offsetDays !== undefined && formData.timeDeclaredZulu) {
      const dueDate = addDaysToDateZulu(formData.timeDeclaredZulu, offsetDays);
      if (dueDate) updateField('dueDateZulu', dueDate);
    }
  };

  return (
    <>
      <div className="field-row">
        <label className="field-label" htmlFor="deferral-reference">
          Deferral Reference
        </label>
        <div className="field-group">
          <input
            id="deferral-reference"
            className="field-input"
            type="text"
            value={formData.deferralReference ?? ''}
            disabled={isReadOnly}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              updateField('deferralReference', event.target.value);
            }}
          />
        </div>
      </div>
      <div className="field-row irops-row">
        <label
          className={`field-label field-label-vcenter${categoryMissing ? ' label-required-missing' : ''}`}
        >
          Deferred Category
        </label>
        <div className="field-group irops-group">
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={!!formData.apuDeferral}
              disabled={isReadOnly}
              onChange={(e) => updateField('apuDeferral', e.target.checked)}
            />
            APU Deferrals
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={!!formData.catIiiRestricted}
              disabled={isReadOnly}
              onChange={(e) => updateField('catIiiRestricted', e.target.checked)}
            />
            CAT III Restricted Aircraft
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={!!formData.etopsRestricted}
              disabled={isReadOnly}
              onChange={(e) => updateField('etopsRestricted', e.target.checked)}
            />
            ETOPS Restricted Aircraft
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={!!formData.operationallyRestrictive}
              disabled={isReadOnly}
              onChange={(e) => updateField('operationallyRestrictive', e.target.checked)}
            />
            Operationally Restrictive Aircraft
          </label>
        </div>
      </div>
      <div className="field-row irops-row">
        <label
          className={`field-label field-label-vcenter${catMissing ? ' label-required-missing' : ''}`}
        >
          CAT
        </label>
        <div className="field-group irops-group">
          {CAT_OPTIONS.map((cat) => (
            <label className="checkbox-field" key={cat}>
              <input
                type="checkbox"
                checked={formData.catType === cat}
                disabled={isReadOnly}
                onChange={() => handleCatChange(cat)}
              />
              {cat}
            </label>
          ))}
        </div>
      </div>
    </>
  );
};

export default DeferredSection;
